/**
 * Payment Portal — Types & Constants
 * All "enums" are implemented as frozen objects for runtime safety (JS project, not TS).
 */

// ---------------------------------------------------------------------------
// Payment Method Enum
// ---------------------------------------------------------------------------

/** @enum {string} */
export const PAYMENT_METHODS = Object.freeze({
    CARD: 'CARD',
    UPI: 'UPI',
    NET_BANKING: 'NET_BANKING',
    WALLET: 'WALLET',
    SAVED: 'SAVED',
});

// ---------------------------------------------------------------------------
// Payment Status Enum
// ---------------------------------------------------------------------------

/** @enum {string} */
export const PAYMENT_STATUS = Object.freeze({
    IDLE: 'IDLE',
    PROCESSING: 'PROCESSING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
});

// ---------------------------------------------------------------------------
// Banks & Wallets
// ---------------------------------------------------------------------------

/** @type {Array<{id: string, name: string, logo: string}>} */
export const POPULAR_BANKS = Object.freeze([
    { id: 'sbi', name: 'State Bank of India', logo: '🏦' },
    { id: 'hdfc', name: 'HDFC Bank', logo: '🏦' },
    { id: 'icici', name: 'ICICI Bank', logo: '🏦' },
    { id: 'axis', name: 'Axis Bank', logo: '🏦' },
    { id: 'kotak', name: 'Kotak Mahindra', logo: '🏦' },
    { id: 'pnb', name: 'Punjab National Bank', logo: '🏦' },
]);

/** @type {Array<{id: string, name: string, color: string}>} */
export const WALLETS = Object.freeze([
    { id: 'paytm', name: 'Paytm', color: '#00BAF2' },
    { id: 'phonepe', name: 'PhonePe', color: '#5F259F' },
    { id: 'mobikwik', name: 'MobiKwik', color: '#1C3A69' },
    { id: 'freecharge', name: 'FreeCharge', color: '#FC4242' },
    { id: 'amazon_pay', name: 'Amazon Pay', color: '#FF9900' },
    { id: 'jio_money', name: 'JioMoney', color: '#009EE2' },
]);

/** @type {Array<{id: string, name: string}>} */
export const UPI_APPS = Object.freeze([
    { id: 'gpay', name: 'Google Pay' },
    { id: 'phonepe', name: 'PhonePe' },
    { id: 'paytm', name: 'Paytm' },
    { id: 'bhim', name: 'BHIM' },
]);

/** @type {Array<{id: string, name: string}>} */
export const ALL_BANKS = Object.freeze([
    ...POPULAR_BANKS,
    { id: 'bob', name: 'Bank of Baroda', logo: '🏦' },
    { id: 'canara', name: 'Canara Bank', logo: '🏦' },
    { id: 'union', name: 'Union Bank of India', logo: '🏦' },
    { id: 'idfc', name: 'IDFC First Bank', logo: '🏦' },
    { id: 'yes', name: 'Yes Bank', logo: '🏦' },
    { id: 'federal', name: 'Federal Bank', logo: '🏦' },
    { id: 'rbl', name: 'RBL Bank', logo: '🏦' },
    { id: 'indusind', name: 'IndusInd Bank', logo: '🏦' },
]);

// ---------------------------------------------------------------------------
// JSDoc Typedefs
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} OrderItem
 * @property {string} id
 * @property {string} name
 * @property {number} quantity
 * @property {number} price
 */

/**
 * @typedef {Object} OrderSummary
 * @property {OrderItem[]} items
 * @property {number} subtotal
 * @property {number} tax
 * @property {number} discount
 * @property {number} total
 * @property {string|null} couponCode
 */

/**
 * @typedef {Object} CardFormData
 * @property {string} cardholderName
 * @property {string} cardNumber
 * @property {string} expiryDate
 * @property {string} cvv
 * @property {boolean} saveCard
 * @property {boolean} hasBillingAddress
 * @property {BillingAddress} [billingAddress]
 */

/**
 * @typedef {Object} BillingAddress
 * @property {string} line1
 * @property {string} [line2]
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {string} country
 */

/**
 * @typedef {Object} PaymentResult
 * @property {boolean} success
 * @property {string} [transactionId]
 * @property {string} [error]
 * @property {string} [redirectUrl]
 */

/**
 * @typedef {Object} CouponResult
 * @property {boolean} valid
 * @property {string} [code]
 * @property {number} [discountAmount]
 * @property {number} [discountPercent]
 * @property {string} [error]
 */

/**
 * @typedef {Object} SavedPaymentMethod
 * @property {string} id
 * @property {'card'|'upi'|'wallet'} type
 * @property {string} [lastFour]
 * @property {string} [brand]
 * @property {string} [expiryDate]
 * @property {string} [upiId]
 * @property {string} [walletName]
 * @property {boolean} isDefault
 */
